/**
 * Core component for rendering SDTM screens.
 * @packageDocumentation 
 */
import React, { useState, useEffect, useContext } from 'react';
import Section from '../../components/Section/Section';
import { useParams, useLocation, Link } from 'react-router-dom';
import { getPreferredTab, getQueryString } from '../../utils/LinkUtils';
import { Stack } from '@fluentui/react/lib/Stack';
import { Label } from '@fluentui/react/lib/Label';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import ClassDatasetSelector from './components/ClassDatasetSelector';
import ClassDatasetTabs from './components/ClassDatasetTabs';
import VariablesTabs from './components/VariablesTabs';
import { PivotKey } from '../../models/PivotKey';
import useSDTMData from './hooks/useSDTMData';
import useSDTMRouteObserver from './hooks/useSDTMRouteObserver';
import AppContext from '../../components/AppContext/AppContext';
import { TabulationType } from '../../models/TabulationType';
import StandardExportButton from '../../components/ExportButton/StandardExportButton';

export const getLink = (standard: string, version: string, classId?: string, datasetId?: string, keepCurrentParams: boolean = false): string => {
  let link = `/mdr/sdtm/${version}`;
  if (classId) link += `/classes/${classId}`;
  if (datasetId) link += `/datasets/${datasetId}`;
  if (keepCurrentParams) link += getQueryString(window.location);
  return link;
}

const defaultPageTitle = "SDTM";

const SDTMComponent: React.FunctionComponent = () => {
  const location = useLocation();
  const { standard, version, classId, datasetId } = useParams<any>();
  const [selectedMetaPivotKey, setSelectedMetaPivotKey] = useState<PivotKey>(PivotKey.Class);
  const [selectedListPivotKey, setSelectedListPivotKey] = useState<PivotKey>(PivotKey.Class);
  const [pageTitle, setPageTitle] = useState<string>(defaultPageTitle);
  const { dataService } = useContext(AppContext);

  // Fetch Page Title
  useEffect(() => {
    dataService.nav.getNavLinkFromHref(getLink(standard, version)).then(page => {
      if (page) setPageTitle(page.shortTitle)
    });
  }, [version]);

  // Fetch SDTM Data
  const {
    isLoadingItem, isLoadingClass, isLoadingDataset, isLoadingItemImplementedBy,
    currentItem, currentClass, currentDataset, currentItemImplementedBy,
  } = useSDTMData(version, classId, datasetId);

  // Handle SDTM Route Changes
  useSDTMRouteObserver(standard, version, classId, datasetId, currentItem, getLink);

  // Update Selected Tab
  useEffect(() => {
    const preferredTab = getPreferredTab(location);
    if (preferredTab) {
      setSelectedMetaPivotKey(preferredTab);
      setSelectedListPivotKey(preferredTab);
    }
    else if (currentDataset) {
      setSelectedMetaPivotKey(PivotKey.Dataset);
      setSelectedListPivotKey(PivotKey.Dataset);
    }
    else {
      setSelectedMetaPivotKey(PivotKey.Class);
      setSelectedListPivotKey(PivotKey.Class);
    }
  }, [currentClass, currentDataset]);

  return (
    <>
      <Section pageTitle={pageTitle}>
        {!isLoadingItem && !isLoadingItemImplementedBy && currentItem
          ? <>
            <Stack horizontal horizontalAlign='space-between'>
              <Stack horizontal tokens={{ childrenGap: 20 }}>
                <Stack>
                  <Label>Status</Label>
                  <span className={currentItem.registrationStatus === "Draft" ? "draft-status" : ""}>{currentItem.registrationStatus}</span>
                </Stack>
                <Stack>
                  <Label>Effective Date</Label>
                  <span>{currentItem.effectiveDate}</span>
                </Stack>
                {currentItemImplementedBy.length > 0 && (
                  <Stack>
                    <Label>Implemented By</Label>
                    <Stack horizontal tokens={{ childrenGap: 14 }}>
                      {currentItemImplementedBy.map((link, index) => (
                        <Link key={`sdtm-ib-${index}-${link.href}`} to={link.href}>
                          {link.shortTitle}
                        </Link>
                      ))}
                    </Stack>
                  </Stack>
                )}
              </Stack>
              <StandardExportButton
                pageTitle={pageTitle}
                standard={standard}
                version={version}
              />
            </Stack>
          </>
          : <>
            {[1, 2].map(idx => (
              <Shimmer key={idx} styles={{ root: { minHeight: 17 } }} shimmerElements={[
                { type: ShimmerElementType.line, width: 50, height: 8 },
                { type: ShimmerElementType.gap, width: 20 },
                { type: ShimmerElementType.line, width: 50, height: 8 },
                { type: ShimmerElementType.gap, width: 20 },
                { type: ShimmerElementType.line, width: 50, height: 8 },
                { type: ShimmerElementType.gap, width: '100%' },
              ]} />
            ))}
          </>
        }
      </Section>

      <ClassDatasetSelector
        classId={classId}
        datasetId={datasetId}
        datasets={currentClass ? currentClass.datasets : null}
        classes={currentItem ? currentItem.classes : null}
        isLoadingClass={isLoadingClass}
        isLoadingItem={isLoadingItem}
        standard={standard}
        version={version}
        getLink={getLink}
      />

      {undefined !== isLoadingClass && (
        <ClassDatasetTabs
          currentClass={currentClass}
          currentDataset={currentDataset}
          isLoadingClass={isLoadingClass}
          isLoadingDataset={isLoadingDataset}
          selectedPivotKey={selectedMetaPivotKey}
          setSelectedPivotKey={setSelectedMetaPivotKey}
          isLoadingSections={false}
          sections={null}
          standardType={TabulationType.SDTM.toLowerCase()}
          version={version}
        />
      )}

      {undefined !== isLoadingClass && (
        <VariablesTabs
          isIG={false}
          currentClass={currentClass}
          currentDataset={currentDataset}
          isLoadingClass={isLoadingClass}
          isLoadingDataset={isLoadingDataset}
          selectedPivotKey={selectedListPivotKey}
          setSelectedPivotKey={setSelectedListPivotKey}
        />
      )}

    </>
  );
}

export default SDTMComponent;
